import { useState } from 'react';
import { runInBackground } from '../../../../configuration/setup/backgroundActions';
import { useAppSelector } from '../../../../configuration/setup/hooks';
import { usePostTransportAssignmentBulkUpdatePreviewMutation } from '../../api/transportOrderApi';
import { handleQueryError } from '../../notifications/ErrorNotification';
import { getTransportAssignmentBulkSelection } from '../../reducers/transportAssignmentsSlice';
import { TransportAssignmentBulkUpdatePreviewResult } from '../transportAssignment.types';
import { StartAssignmentBulkUpdateButton } from './StartAssignmentBulkUpdateButton';
import { TransportOrderBulkUpdateDialog } from './TransportOrderBulkUpdateDialog';
import { DuplicateVinErrorResponseCodec } from '../../api/types/transportAssignment/errorCodes';

export const MAX_BULK_SELECTION_SIZE = 1000;

type TransportOrderBulkUpdateState = {
    readonly openDialog: boolean;
    readonly previewResult?: TransportAssignmentBulkUpdatePreviewResult;
};

const initialTransportOrderBulkUpdateState: TransportOrderBulkUpdateState = {
    openDialog: false,
    previewResult: undefined,
};

export const TransportOrderBulkUpdate = () => {
    const selectedIds = useAppSelector(getTransportAssignmentBulkSelection);
    const maxBulkSelectionExceeded = selectedIds.length > MAX_BULK_SELECTION_SIZE;
    const [postTransportAssignmentBulkUpdatePreview, transportAssignmentBulkUpdatePreviewRequest] =
        usePostTransportAssignmentBulkUpdatePreviewMutation();
    const [bulkUpdateState, setBulkUpdateState] = useState(initialTransportOrderBulkUpdateState);

    const openDialog = () => {
        if (selectedIds.length === 0) {
            return;
        }

        if (maxBulkSelectionExceeded) {
            setBulkUpdateState(() => ({
                openDialog: true,
                previewResult: { success: false, errorCode: 'size-exceeded' },
            }));
            return;
        }

        runInBackground(async () => {
            try {
                const updatePreview = await postTransportAssignmentBulkUpdatePreview({
                    assignmentIds: selectedIds,
                }).unwrap();
                setBulkUpdateState(() => ({
                    openDialog: true,
                    previewResult: { success: true, ...updatePreview },
                }));
            } catch (error) {
                if (DuplicateVinErrorResponseCodec.is(error)) {
                    setBulkUpdateState(() => ({
                        openDialog: true,
                        previewResult: { success: false, errorCode: 'duplicate-vin' },
                    }));
                } else {
                    handleQueryError(error);
                }
            }
        });
    };

    const closeDialog = () =>
        setBulkUpdateState(() => ({
            ...initialTransportOrderBulkUpdateState,
        }));

    return (
        <>
            <StartAssignmentBulkUpdateButton
                isLoading={transportAssignmentBulkUpdatePreviewRequest.isLoading}
                count={selectedIds.length}
                onClick={openDialog}
            />
            {bulkUpdateState.openDialog && bulkUpdateState.previewResult && (
                <TransportOrderBulkUpdateDialog previewResult={bulkUpdateState.previewResult} onClose={closeDialog} />
            )}
        </>
    );
};
